import React, { useContext, useRef, useState } from 'react'
import {
  Alert, Box, Button, Card, CardContent, Checkbox, Chip, CircularProgress, IconButton, MenuItem,
  Stack, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination,
  TableRow, TableSortLabel, Tabs, Tab, TextField, Typography,
} from '@mui/material'
import { Delete, Edit } from '@mui/icons-material'
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useOktaAuth } from '@okta/okta-react'
import { Price, Program } from '@nxcr-org/web-api-interface/lib/billing_service_pb'
import { FeatureFlags } from '../../../../config/flags'
import { NexusService } from 'global-apis/nexus-service'
import { ClientContext } from '../../../../utils/clientContext'
import { formatCents } from '../../../../utils/currency-formatter'
import { ProgramForm } from './ProgramForm'
import { PriceForm } from './PriceForm'
import {
  canManagePrograms, filterPrograms, getProgramPrices, ProgramPrice, ProgramSort, UUID_PATTERN,
  getPriceLimitError, getPriceScheduleStatus, INCOMPLETE_PRICES_MESSAGE, PRICE_LIMIT_MESSAGE,
  PriceSort, sortPrices,
} from './programs.utils'

type FormState = { kind: 'program'; program?: Program } | { kind: 'price'; price?: Price }

function priceMessage(row: ProgramPrice, programId: string): Price {
  const price = new Price()
  price.setId(row.id)
  price.setProgramId(programId)
  price.setReservationFee(row.reservationFee)
  price.setDeposit(row.deposit)
  price.setStartFee(row.startFee)
  price.setMonthlyRent(row.monthlyRent)
  price.setPriceTermMonths(row.priceTermMonths)
  price.setFromDate(row.fromDate)
  price.setToDate(row.toDate)
  price.setHidden(row.hidden)
  return price
}

function money(value?: number) {
  return value === undefined ? 'Not provided' : formatCents(value)
}

function date(value?: number) {
  if (value === undefined) return 'Not provided'
  if (value === 0) return 'Unbounded'
  return Math.abs(value) <= 8640000000000000 ? new Date(value).toLocaleString() : 'Invalid date'
}

export function Programs() {
  const context = useContext(ClientContext)
  const { authState } = useOktaAuth()
  const clientId = context?.clientId || ''
  const canManage = canManagePrograms(authState?.idToken?.claims?.groups, process.env.NEXUS_ADMIN_GROUPS)
  if (!clientId) return <Alert severity="info">Select a client to view programs.</Alert>
  return <ClientPrograms key={clientId} clientId={clientId} canManage={canManage} canCreate={canManage && FeatureFlags.programCreation} />
}

function ClientPrograms({ clientId, canManage, canCreate }: { clientId: string; canManage: boolean; canCreate: boolean }) {
  const { programId } = useParams<{ programId: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const access = useRef({ clientId, canManage, canCreate, mounted: false })
  access.current.clientId = clientId
  access.current.canManage = canManage
  access.current.canCreate = canCreate
  React.useEffect(() => {
    access.current.mounted = true
    return () => { access.current.mounted = false }
  }, [])
  const [search, setSearch] = useState('')
  const [make, setMake] = useState('')
  const [model, setModel] = useState('')
  const [visibility, setVisibility] = useState('')
  const [active, setActive] = useState('active')
  const [sort, setSort] = useState<ProgramSort>('name')
  const [direction, setDirection] = useState<'asc' | 'desc'>('asc')
  const [priceSort, setPriceSort] = useState<PriceSort>('monthlyRent')
  const [priceDirection, setPriceDirection] = useState<'asc' | 'desc'>('asc')
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [tab, setTab] = useState('overview')
  const [form, setForm] = useState<FormState | null>(null)
  const [saved, setSaved] = useState('')
  const [deleteError, setDeleteError] = useState(false)
  const deleting = useRef(false)
  const savingPrice = useRef(false)
  const query = useQuery({
    queryKey: ['programs', clientId],
    queryFn: () => NexusService.listPrograms(clientId),
  })
  const programs = query.data || []
  const selectedProgram = programs.find(program => program.getId() === programId)
  const summaries = programs.map(program => program.toObject())
  const filtered = filterPrograms(summaries, search, make, model, visibility, active, sort, direction)
  const currentPage = Math.min(page, Math.max(0, Math.ceil(filtered.length / rowsPerPage) - 1))
  const prices = selectedProgram ? getProgramPrices(selectedProgram.toObject().pricingOptionsList) : []
  const sortedPrices = sortPrices(prices, priceSort, priceDirection)
  const scheduleStatus = getPriceScheduleStatus(prices)

  const checkAccess = () => {
    if (!access.current.mounted || !access.current.canManage || access.current.clientId !== clientId) {
      throw new Error('Program editing is not permitted for the selected client.')
    }
  }
  const refresh = () => Promise.all([
    queryClient.invalidateQueries({ queryKey: ['programs', clientId] }),
    queryClient.invalidateQueries({ queryKey: ['/listPrograms', clientId] }),
    queryClient.invalidateQueries({ queryKey: ['/programOptions', clientId] }),
  ])
  const programMutation = useMutation({
    mutationFn: async (program: Program) => {
      checkAccess()
      if (program.getClientId() !== clientId) throw new Error('Client mismatch.')
      if (program.getId() && !programs.some(item => item.getId() === program.getId())) {
        throw new Error('Program does not belong to this client.')
      }
      if (!program.getId() && !access.current.canCreate) {
        throw new Error('Program creation is not available in this environment.')
      }
      return program.getId() ? NexusService.updateProgram(program) : NexusService.createProgram(program)
    },
    onSuccess: async (program) => {
      await refresh()
      if (!access.current.mounted) return
      setSaved('Program saved')
      setForm(null)
      navigate(`/programs/${encodeURIComponent(program.getId())}`)
    },
  })
  const priceMutation = useMutation({
    mutationFn: async (price: Price) => {
      checkAccess()
      if (!selectedProgram || price.getProgramId() !== selectedProgram.getId()) {
        throw new Error('Program does not belong to this client.')
      }
      if (savingPrice.current) throw new Error('A price save is already in progress.')
      savingPrice.current = true
      try {
        // Re-read before saving rather than trusting the snapshot from when the form opened.
        // Atomic enforcement across clients still belongs in the billing service.
        const latest = await NexusService.listPrograms(clientId)
        checkAccess()
        const program = latest.find(item => item.getId() === price.getProgramId())
        if (!program) throw new Error('Program does not belong to this client.')
        const latestPrices = getProgramPrices(program.toObject().pricingOptionsList)
        if (price.getId() && !latestPrices.some(item => item.id === price.getId())) {
          throw new Error('Price no longer belongs to this program. Refresh pricing.')
        }
        const error = getPriceLimitError(latestPrices, price.toObject())
        if (error) throw new Error(error)
        return await (price.getId() ? NexusService.updatePrice(price) : NexusService.createPrice(price))
      } finally {
        savingPrice.current = false
      }
    },
    onSuccess: async () => {
      await refresh()
      if (!access.current.mounted) return
      setSaved('Price saved')
      setForm(null)
    },
  })

  const deletePrice = async (row: ProgramPrice) => {
    if (deleting.current || priceMutation.isPending || !row.editable) return
    if (!window.confirm('Delete this price? Its end date will be set to now; the record will be retained.')) return
    deleting.current = true
    setDeleteError(false)
    try {
      const price = priceMessage(row, programId)
      price.setToDate(Date.now())
      await priceMutation.mutateAsync(price)
    } catch {
      if (access.current.mounted) setDeleteError(true)
    } finally {
      deleting.current = false
    }
  }

  const sortBy = (column: ProgramSort) => {
    setDirection(sort === column && direction === 'asc' ? 'desc' : 'asc')
    setSort(column)
    setPage(0)
  }
  const sortPricesBy = (column: PriceSort) => {
    setPriceDirection(priceSort === column && priceDirection === 'asc' ? 'desc' : 'asc')
    setPriceSort(column)
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2} sx={{ mb: 2 }}>
        <Typography variant="h4" component="h1">{programId ? 'Program details' : 'Programs'}</Typography>
        {programId
          ? <Button component={RouterLink} to="/programs">All programs</Button>
          : canCreate && <Button variant="contained" disabled={query.isPending || query.isError}
            onClick={() => setForm({ kind: 'program' })}>Create program</Button>}
      </Stack>
      {!canManage && <Alert severity="info" sx={{ mb: 2 }}>Read-only access. A configured program administrator can manage programs and prices.</Alert>}
      {saved && <Alert severity="success" onClose={() => setSaved('')} sx={{ mb: 2 }}>{saved}</Alert>}
      {query.isPending && <Stack direction="row" spacing={2} role="status"><CircularProgress size={24} /><Typography>Loading programs…</Typography></Stack>}
      {query.isError && <Alert severity="error" action={<Button color="inherit" disabled={query.isFetching}
        onClick={() => query.refetch()}>Retry</Button>}>Programs could not be loaded.</Alert>}
      {!query.isPending && !query.isError && (programId ? (
        !selectedProgram ? <Alert severity="info">Program not found for the selected client.</Alert> : (
          <>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} sx={{ mb: 2 }}>
              <Typography variant="h5" component="h2">{selectedProgram.getName() || selectedProgram.getDescription()}</Typography>
              <Chip label={selectedProgram.getHidden() ? 'Hidden' : 'Visible'} />
              <Chip label={selectedProgram.getActive() ? 'Active' : 'Inactive'} />
              {canManage && UUID_PATTERN.test(programId) &&
                <Button onClick={() => setForm({ kind: 'program', program: selectedProgram })}>Edit program</Button>}
            </Stack>
            <Tabs value={tab} onChange={(_, value) => setTab(value)} aria-label="Program sections">
              <Tab value="overview" label="Overview" id="program-overview-tab" aria-controls="program-overview-panel" />
              <Tab value="pricing" label="Pricing" id="program-pricing-tab" aria-controls="program-pricing-panel" />
            </Tabs>
            {tab === 'overview' ? (
              <Card role="tabpanel" id="program-overview-panel" aria-labelledby="program-overview-tab">
                <CardContent>
                  <Box component="dl" sx={{ m: 0, '& dt': { fontWeight: 'bold', mt: 2 }, '& dd': { m: 0, overflowWrap: 'anywhere' } }}>
                    {[
                      ['Program ID', selectedProgram.getId()],
                      ['Description', selectedProgram.getDescription()],
                      ['Make', selectedProgram.getMake()], ['Model', selectedProgram.getModel()],
                      ['Type', selectedProgram.getType()], ['Program group', selectedProgram.getProgramGroup()],
                      ['Featured', selectedProgram.getFeatured() ? `Yes (order ${selectedProgram.getFeaturedOrder()})` : 'No'],
                      ['Trim options', selectedProgram.getTrimOptions()],
                      ['Client ID', selectedProgram.getClientId()],
                    ].map(([label, value]) => <React.Fragment key={label}><Typography component="dt">{label}</Typography><Typography component="dd">{value || '—'}</Typography></React.Fragment>)}
                  </Box>
                </CardContent>
              </Card>
            ) : (
              <Box role="tabpanel" id="program-pricing-panel" aria-labelledby="program-pricing-tab" sx={{ pt: 2 }}>
                {scheduleStatus.exceedsLimit && <Alert severity="warning" sx={{ mb: 2 }}>{PRICE_LIMIT_MESSAGE} Hide or expire an existing price to resolve the conflict.</Alert>}
                {scheduleStatus.hasIncompletePrices && <Alert severity="warning" sx={{ mb: 2 }}>{INCOMPLETE_PRICES_MESSAGE}</Alert>}
                {deleteError && <Alert severity="error" sx={{ mb: 2 }}>Unable to delete the price. Please try again.</Alert>}
                {canManage && UUID_PATTERN.test(programId) &&
                  <Button variant="contained" sx={{ mb: 2 }} disabled={priceMutation.isPending} onClick={() => setForm({ kind: 'price' })}>Add price</Button>}
                {!prices.length ? <Alert severity="info">No pricing options for this program.</Alert> : (
                  <TableContainer component={Card}>
                    <Table aria-label="Program prices">
                      <TableHead><TableRow>
                        {([
                          ['monthlyRent', 'Monthly rent'],
                          ['startFee', 'Start fee'],
                          ['deposit', 'Deposit'],
                          ['reservationFee', 'Reservation fee'],
                          ['fromDate', 'From (local time)'],
                          ['toDate', 'To (local time)'],
                          ['hidden', 'Hidden'],
                        ] as [PriceSort, string][]).map(([column, label]) => (
                          <TableCell key={column} sortDirection={priceSort === column ? priceDirection : false}>
                            <TableSortLabel active={priceSort === column}
                              direction={priceSort === column ? priceDirection : 'asc'}
                              onClick={() => sortPricesBy(column)}>{label}</TableSortLabel>
                          </TableCell>
                        ))}
                        <TableCell>Actions</TableCell>
                      </TableRow></TableHead>
                      <TableBody>{sortedPrices.map((price, index) => (
                        <TableRow key={`${price.id}-${price.fromDate}-${index}`}>
                          <TableCell>{money(price.monthlyRent)}</TableCell><TableCell>{money(price.startFee)}</TableCell>
                          <TableCell>{money(price.deposit)}</TableCell><TableCell>{money(price.reservationFee)}</TableCell>
                          <TableCell>{date(price.fromDate)}</TableCell><TableCell>{date(price.toDate)}</TableCell>
                          <TableCell>{price.hidden === undefined ? 'Not provided' :
                            <Checkbox checked={price.hidden} disabled slotProps={{ input: { 'aria-label': `Hidden price ${price.id}` } }} />}</TableCell>
                          <TableCell>{canManage && (
                            <Stack direction="row">
                              <IconButton aria-label={`Edit price ${price.id}`} disabled={priceMutation.isPending}
                                onClick={() => setForm({ kind: 'price', price: priceMessage(price, programId) })}><Edit /></IconButton>
                              <IconButton aria-label={`Delete price ${price.id}`} disabled={priceMutation.isPending}
                                onClick={() => deletePrice(price)}><Delete /></IconButton>
                            </Stack>)}</TableCell>
                        </TableRow>
                      ))}</TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>
            )}
          </>
        )
      ) : (
        <>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ my: 2 }}>
            <TextField label="Search programs" value={search} onChange={event => { setSearch(event.target.value); setPage(0) }} />
            <TextField select label="Make" value={make} sx={{ minWidth: 140 }} onChange={event => {
              setMake(event.target.value); setModel(''); setPage(0)
            }}><MenuItem value="">All makes</MenuItem>{Array.from(new Set(summaries.map(program => program.make).filter(Boolean))).sort()
                .map(value => <MenuItem key={value} value={value}>{value}</MenuItem>)}</TextField>
            <TextField select label="Model" value={model} sx={{ minWidth: 140 }} onChange={event => { setModel(event.target.value); setPage(0) }}>
              <MenuItem value="">All models</MenuItem>{Array.from(new Set(summaries.filter(program => !make || program.make === make).map(program => program.model).filter(Boolean))).sort()
                .map(value => <MenuItem key={value} value={value}>{value}</MenuItem>)}</TextField>
            <TextField select label="Visibility" value={visibility} sx={{ minWidth: 140 }} onChange={event => { setVisibility(event.target.value); setPage(0) }}>
              <MenuItem value="">All visibility</MenuItem><MenuItem value="visible">Visible</MenuItem><MenuItem value="hidden">Hidden</MenuItem>
            </TextField>
            <TextField select label="Status" value={active} sx={{ minWidth: 140 }} onChange={event => { setActive(event.target.value); setPage(0) }}>
              <MenuItem value="active">Active</MenuItem><MenuItem value="inactive">Inactive</MenuItem><MenuItem value="">All statuses</MenuItem>
            </TextField>
          </Stack>
          {!filtered.length ? <Alert severity="info">{programs.length ? 'No programs match these filters.' : 'No programs for this client.'}</Alert> : (
            <Card>
              <TableContainer>
                <Table aria-label="Programs">
                  <TableHead><TableRow>
                    {(['name', 'make', 'model', 'visibility'] as ProgramSort[]).map(column => (
                      <TableCell key={column} sortDirection={sort === column ? direction : false}>
                        <TableSortLabel active={sort === column} direction={sort === column ? direction : 'asc'} onClick={() => sortBy(column)}>
                          {column === 'name' ? 'Program / description' : column.charAt(0).toUpperCase() + column.slice(1)}
                        </TableSortLabel>
                      </TableCell>
                    ))}
                    <TableCell>All monthly prices (including hidden)</TableCell>
                  </TableRow></TableHead>
                  <TableBody>{filtered.slice(currentPage * rowsPerPage, (currentPage + 1) * rowsPerPage).map(program => {
                    const amounts = getProgramPrices(program.pricingOptionsList).map(price => price.monthlyRent).filter(amount => amount !== undefined)
                    return <TableRow key={program.id}>
                      <TableCell><Button component={RouterLink} to={`/programs/${encodeURIComponent(program.id)}`}>{program.name || program.description || program.id}</Button>
                        <Typography variant="body2">{program.description}</Typography></TableCell>
                      <TableCell>{program.make || '—'}</TableCell><TableCell>{program.model || '—'}</TableCell>
                      <TableCell>{program.hidden ? 'Hidden' : 'Visible'}</TableCell>
                      <TableCell>{amounts.length ? `${formatCents(Math.min(...amounts))}–${formatCents(Math.max(...amounts))} / month` : 'No pricing'}</TableCell>
                    </TableRow>
                  })}</TableBody>
                </Table>
              </TableContainer>
              <TablePagination component="div" count={filtered.length} page={currentPage} rowsPerPage={rowsPerPage}
                rowsPerPageOptions={[10, 25, 50]} onPageChange={(_, value) => setPage(value)}
                onRowsPerPageChange={event => { setRowsPerPage(Number(event.target.value)); setPage(0) }} />
            </Card>
          )}
        </>
      ))}
      {canCreate && form?.kind === 'program' &&
        <ProgramForm open program={form.program} clientId={clientId} onClose={() => setForm(null)}
          onSave={async program => { await programMutation.mutateAsync(program) }} />}
      {canManage && form?.kind === 'price' && selectedProgram &&
        <PriceForm open price={form.price} prices={prices} programId={selectedProgram.getId()} onClose={() => setForm(null)}
          onSave={async price => { await priceMutation.mutateAsync(price) }} />}
    </Box>
  )
}
